(function () {
  var zoomData = require('./zoomlevels.json');

  /**
   * Simple OO API client
   */
  function ApiClient(basePath) {
    this.basePath = basePath || "/api";
  }

  ApiClient.prototype.request = function (endpoint, opts) {
    var method = (opts && opts.method) || "GET";
    var headers = (opts && opts.headers) || { Accept: "application/json" };
    var body = opts && opts.body ? JSON.stringify(opts.body) : null;
    var url =
      this.basePath.replace(/\/$/, "") + "/" + endpoint.replace(/^\//, "");

    if (method.toUpperCase() === "GET" && opts && opts.params) {
      var params = new URLSearchParams();
      Object.keys(opts.params).forEach(function (k) {
        if (opts.params[k] !== undefined && opts.params[k] !== "") {
          params.append(k, opts.params[k]);
        }
      });
      var q = params.toString();
      if (q) url += "?" + q;
    }

    return fetch(url, {
      method: method,
      headers: headers,
      body: body,
    }).then(function (res) {
      if (!res.ok)
        throw new Error("Network response was not ok: " + res.status);
      return res.json();
    });
  };

  /**
   * Map manager: load map, clear pins, add pins + cluster
   */
  function MapManager(mapElId, options) {
    this.mapElId = mapElId;
    this.map = null;
    this.markers = [];
    this.markerCluster = null;
    this.options = options || {};
    this.infoWindow = null;
  }

  MapManager.prototype.loadMap = function (mapOptions) {
    var defaults = {
      zoom: 4,
      center: { lat: 39.5, lng: -98.35 },
      mapId: "DEMO_MAP_ID",
    };
    var opts = Object.assign({}, defaults, mapOptions || {});
    this.map = new google.maps.Map(document.getElementById(this.mapElId), opts);
    this.infoWindow = new google.maps.InfoWindow({ content: "" });
  };

  MapManager.prototype.clearPins = function () {
    if (this.markers && this.markers.length) {
      this.markers.forEach(function (m) {
        m.map = null;
      });
      this.markers = [];
    }
    if (
      this.markerCluster &&
      typeof this.markerCluster.clearMarkers === "function"
    ) {
      this.markerCluster.clearMarkers();
      this.markerCluster = null;
    }
  };

  MapManager.prototype.addPins = function (locations) {
    var self = this;
    if (!this.map) return;
    this.clearPins();

    if (!Array.isArray(locations) || locations.length === 0) return;

    this.markers = locations
      .map(function (loc, i) {
        var lat = parseFloat(loc.lat);
        var lng = parseFloat(loc.lng);
        if (Number.isNaN(lat) || Number.isNaN(lng)) return null;

        var marker = new google.maps.marker.AdvancedMarkerElement({
          position: { lat: lat, lng: lng },
          map: self.map,
          title: loc.title || "Location " + (i + 1),
          gmpClickable: true,
        });

        marker.addListener("gmp-click", function () {
          var html =
            '<div style="width:300px; padding: 0 15px 21px;">' +
            '<div style="text-align: left;font: normal normal bold 14px/19px Montserrat;letter-spacing: 0px;color: #002C6B;">' +
            (loc.address && loc.address.address1
              ? "<div>" + escapeHtml(loc.address.address1) + "</div>"
              : "") +
            (loc.address && loc.address.city
              ? "<div>" +
                escapeHtml(loc.address.city) +
                ", " +
                escapeHtml(loc.address.state || "") +
                " " +
                escapeHtml(loc.address.zip || "") +
                "</div>"
              : "") +
            (loc.directions ?
              '<hr style="border: 2px solid #00A0E6; width:32px; margin:10px 0; border-top:0;" />' +
              '<div style="margin-top:6px;"><a style="font: normal normal bold 14px/32px Montserrat;letter-spacing: 0px;color: #59A728;" target="_blank" href="' +
                sanitizeUrl(loc.directions) +
                '">Get directions <span style="color:#59A728; margin-top:2px;"><?xml version="1.0" encoding="utf-8"?><svg width="1rem" height="1rem" viewBox="0 0 24 24" fill="#59A728" xmlns="http://www.w3.org/2000/svg"><path d="M7 17L17 7M17 7H8M17 7V16" stroke="#59A728" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></span></a></div>'
              : "") +
            "</div></div>";
          self.infoWindow.setContent(html);
          self.infoWindow.open({ anchor: marker, map: self.map });
        });

        return marker;
      })
      .filter(function (m) {
        return m !== null;
      });

    // Try to create a clusterer if available
    if (window.MarkerClusterer) {
      try {
        this.markerCluster = new MarkerClusterer({
          map: this.map,
          markers: this.markers,
        });
      } catch (e) {
        // older global: markerClusterer.MarkerClusterer
        if (window.markerClusterer && window.markerClusterer.MarkerClusterer) {
          this.markerCluster = new window.markerClusterer.MarkerClusterer({
            map: this.map,
            markers: this.markers,
          });
        } else {
          // fallback: do nothing
        }
      }
    } else if (
      window.markerClusterer &&
      window.markerClusterer.MarkerClusterer
    ) {
      this.markerCluster = new window.markerClusterer.MarkerClusterer({
        map: this.map,
        markers: this.markers,
      });
    }
  };

  /**
   * LocationsPage: orchestrates fetching, filters, rendering, pagination, map updates, URL state.
   *
   * State model: nothing is queried on field change (mode switch, select, zip, distance) — those
   * only update the form's own inputs. `applied` (a snapshot of {mode,state,zip,radius,service})
   * is only set by Apply/Reset, and only then do we fetch, update the map/results, and push the
   * URL. This mirrors design_handoff_locations_filter/README.md's "Applying" / "State Management"
   * sections. On mobile, field changes still trigger a debounced *count-only* preview fetch to
   * label the Apply button ("Show N locations") without touching `applied`/the map/results.
   */
  function LocationsPage(opts) {
    opts = opts || {};
    this.api = new ApiClient(opts.apiBase || "/api");
    this.mapManager = new MapManager(opts.mapId || "map");
    this.resultsEl = document.getElementById(opts.resultsId || "results");
    this.paginationEl = document.getElementById(
      opts.paginationListId || "pagination-list"
    );
    this.perPage = opts.perPage || 10;
    this.detailsEndpoint = opts.detailsEndpoint || "details";
    this.locationsEndpoint = opts.locationsEndpoint || "locations";

    this.form = document.forms.filters;
    this.modeInputs = document.querySelectorAll('input[name="mode"]');
    this.modeRows = document.querySelectorAll('[data-mode-row]');
    this.stateSelect = document.getElementById("sb-state");
    this.serviceSelect = document.getElementById("sb-services");
    this.zipInput = document.getElementById("sb-zip");
    this.radiusInput = document.getElementById("sb-radius");
    this.pills = document.querySelectorAll(".filters__pill");
    this.distanceValueEl = document.getElementById("distance-value");
    this.fieldState = document.getElementById("field-state");
    this.fieldZip = document.getElementById("field-zip");
    this.fieldDistance = document.getElementById("field-distance");
    this.stateError = document.getElementById("state-error");
    this.zipError = document.getElementById("zip-error");
    this.applyBtn = document.getElementById("filters-apply");
    this.applyLabelEl = document.getElementById("filters-apply-label");
    this.resetBtn = document.getElementById("filters-reset");
    this.editFiltersBtn = document.getElementById("locations-edit-filters");
    this.sheetCloseBtn = document.getElementById("filters-sheet-close");
    this.scrimEl = document.getElementById("locations-scrim");
    this.filtersPanel = document.getElementById("locations-filters");
    this.resultHeadlineEl = document.getElementById("result-headline");
    this.resultDetailEl = document.getElementById("result-detail");

    this.allLocations = []; // entire dataset from the last fetch
    this.filtered = []; // same as allLocations today — server already filters; kept for pagination helpers
    this.currentPage = 1;
    this.zoomLevels = zoomData;
    this.meta = {};
    this.applied = null; // null until Apply/Reset — snapshot of {mode,state,zip,radius,service}
    this.sheetOpen = false;
    this.previewTimer = null;
    this.lastFocusedBeforeSheet = null;
    this.mobileQuery = window.matchMedia("(max-width: 980px)");

    // guards fetchLocations against out-of-order responses: if a filter
    // changes again before the previous request resolves, a slower first
    // response arriving after the second must not overwrite the pins with
    // stale data
    this.latestRequestId = 0;

    // Approximate map zoom level for each selectable search radius (miles)
    this.radiusZoomLevels = { 25: 9, 50: 8, 100: 7, 250: 6 };

    this._handleSheetKeydown = this.handleSheetKeydown.bind(this);
  }

  LocationsPage.prototype.init = function () {
    var self = this;
    this.mapManager.loadMap();

    Array.prototype.forEach.call(this.modeInputs, function (input) {
      input.addEventListener("change", function () {
        self.onModeChange(input.value);
        self.schedulePreview();
      });
    });

    Array.prototype.forEach.call(this.pills, function (pill) {
      pill.addEventListener("click", function () {
        self.onPillClick(pill);
      });
    });

    if (this.zipInput) {
      this.zipInput.addEventListener("input", function () {
        self.schedulePreview();
      });
    }
    if (this.stateSelect) {
      this.stateSelect.addEventListener("change", function () {
        self.schedulePreview();
      });
    }
    if (this.serviceSelect) {
      this.serviceSelect.addEventListener("change", function () {
        self.schedulePreview();
      });
    }

    if (this.applyBtn) {
      this.applyBtn.addEventListener("click", function () {
        self.applyFilters();
      });
    }
    if (this.resetBtn) {
      this.resetBtn.addEventListener("click", function () {
        self.resetFilters();
      });
    }
    if (this.editFiltersBtn) {
      this.editFiltersBtn.addEventListener("click", function () {
        self.openSheet();
      });
    }
    if (this.sheetCloseBtn) {
      this.sheetCloseBtn.addEventListener("click", function () {
        self.closeSheet();
      });
    }
    if (this.scrimEl) {
      this.scrimEl.addEventListener("click", function () {
        self.closeSheet();
      });
    }

    if (this.mobileQuery.addEventListener) {
      this.mobileQuery.addEventListener("change", function () {
        if (!self.mobileQuery.matches) self.closeSheet();
        self.doPreview();
      });
    }

    // pagination click delegate
    if (this.paginationEl) {
      this.paginationEl.addEventListener("click", function (ev) {
        var t = ev.target;
        var btn = t.closest("button[data-number], button[data-page]");
        if (!btn) return;
        var num = btn.getAttribute("data-number");
        var page = btn.getAttribute("data-page");
        if (num === "prev") self.changePage(self.currentPage - 1);
        else if (num === "next") self.changePage(self.currentPage + 1);
        else if (page) self.changePage(parseInt(page, 10));
      });
    }

    // initial load: read url params
    var params = parseQueryString(location.search || "");
    var hasCriteria = !!(params.state || params.zip);
    var mode = params.mode || (params.zip ? "zip" : "state");

    this.setMode(mode);
    if (params.zip) this.zipInput.value = params.zip;
    if (params.radius) this.setRadius(params.radius);
    this.applied = hasCriteria
      ? {
          mode: mode,
          state: params.state || "",
          zip: params.zip || "",
          radius: params.radius || "25",
          service: params.service || "",
        }
      : null;

    // load details for dropdowns, then finish hydrating (state/service
    // <select> options don't exist yet, so setting .value earlier is a no-op)
    this.loadDetails().then(function () {
      if (params.state && self.stateSelect) self.stateSelect.value = params.state;
      if (params.service && self.serviceSelect) self.serviceSelect.value = params.service;
      if (self.applied) {
        self.applied.state = self.stateSelect ? self.stateSelect.value : "";
        self.applied.service = self.serviceSelect ? self.serviceSelect.value : "";
      }

      var apiParams = self.applied ? self.buildApiParams(self.applied) : {};
      self.fetchLocations(apiParams).then(function () {
        self.currentPage = parseInt(params.page, 10) || 1;
        self.updateViewFromState();
      });
    });

    // handle popstate for back/forward
    window.addEventListener("popstate", function () {
      var p = parseQueryString(location.search || "");
      var hasC = !!(p.state || p.zip);
      var m = p.mode || (p.zip ? "zip" : "state");
      self.setMode(m);
      if (self.zipInput) self.zipInput.value = p.zip || "";
      self.setRadius(p.radius || "25");
      if (self.stateSelect) self.stateSelect.value = p.state || "";
      if (self.serviceSelect) self.serviceSelect.value = p.service || "";
      self.applied = hasC
        ? {
            mode: m,
            state: self.stateSelect ? self.stateSelect.value : "",
            zip: p.zip || "",
            radius: p.radius || "25",
            service: self.serviceSelect ? self.serviceSelect.value : "",
          }
        : null;
      var apiParams = self.applied ? self.buildApiParams(self.applied) : {};
      self.fetchLocations(apiParams).then(function () {
        self.currentPage = parseInt(p.page, 10) || 1;
        self.updateViewFromState();
      });
    });
  };

  /**
   * Switch which mode's fields are shown/enabled. Field values are left in
   * the DOM (just hidden), so switching back to a mode restores whatever
   * was previously entered — no separate memory object needed.
   */
  LocationsPage.prototype.setMode = function (mode) {
    Array.prototype.forEach.call(this.modeInputs, function (input) {
      input.checked = input.value === mode;
    });
    this.onModeChange(mode);
  };

  LocationsPage.prototype.onModeChange = function (mode) {
    Array.prototype.forEach.call(this.modeRows, function (row) {
      row.classList.toggle(
        "filters__mode-row--selected",
        row.getAttribute("data-mode-row") === mode
      );
    });
    if (this.fieldState) this.fieldState.hidden = mode !== "state";
    if (this.fieldZip) this.fieldZip.hidden = mode !== "zip";
    if (this.fieldDistance) this.fieldDistance.hidden = mode !== "zip";
    this.hideError(this.stateError);
    this.hideError(this.zipError);
  };

  LocationsPage.prototype.onPillClick = function (pill) {
    this.setRadius(pill.getAttribute("data-radius"));
    this.schedulePreview();
  };

  LocationsPage.prototype.setRadius = function (radius) {
    radius = String(radius);
    if (this.radiusInput) this.radiusInput.value = radius;
    Array.prototype.forEach.call(this.pills, function (pill) {
      pill.classList.toggle(
        "filters__pill--selected",
        pill.getAttribute("data-radius") === radius
      );
    });
    if (this.distanceValueEl) this.distanceValueEl.textContent = radius + " miles";
  };

  LocationsPage.prototype.currentMode = function () {
    var checked = Array.prototype.find.call(this.modeInputs, function (i) {
      return i.checked;
    });
    return checked ? checked.value : "state";
  };

  LocationsPage.prototype.collectFormValues = function () {
    return {
      mode: this.currentMode(),
      state: this.stateSelect ? this.stateSelect.value : "",
      zip: this.zipInput ? String(this.zipInput.value).trim() : "",
      radius: this.radiusInput ? this.radiusInput.value : "25",
      service: this.serviceSelect ? this.serviceSelect.value : "",
    };
  };

  LocationsPage.prototype.showError = function (el, message) {
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  };

  LocationsPage.prototype.hideError = function (el) {
    if (!el) return;
    el.hidden = true;
  };

  /**
   * State mode requires a state; ZIP mode requires a 5-digit ZIP. Service is
   * always optional. Error copy is a provisional placeholder — the design
   * handoff explicitly leaves final validation copy for the designer.
   */
  LocationsPage.prototype.validate = function (values) {
    if (values.mode === "state") {
      if (!values.state) {
        this.showError(this.stateError, "Select a state to search.");
        return false;
      }
      this.hideError(this.stateError);
      return true;
    }
    if (!/^\d{5}$/.test(values.zip)) {
      this.showError(this.zipError, "Enter a valid 5-digit ZIP code.");
      return false;
    }
    this.hideError(this.zipError);
    return true;
  };

  LocationsPage.prototype.buildApiParams = function (values) {
    var params = {};
    if (values.mode === "state") {
      if (values.state) params.state = values.state;
    } else {
      params.zip = values.zip;
      params.radius = values.radius;
    }
    if (values.service) params.service = values.service;
    return params;
  };

  LocationsPage.prototype.applyFilters = function () {
    var values = this.collectFormValues();
    if (!this.validate(values)) return;

    this.applied = values;
    var apiParams = this.buildApiParams(values);
    var urlParams = Object.assign({ mode: values.mode }, apiParams);
    this.pushUrlParams(urlParams);

    var self = this;
    this.fetchLocations(apiParams).then(function () {
      self.updateViewFromState();
      self.closeSheet();
    });
  };

  LocationsPage.prototype.resetFilters = function () {
    this.setMode("state");
    if (this.stateSelect) this.stateSelect.value = "";
    if (this.zipInput) this.zipInput.value = "";
    if (this.serviceSelect) this.serviceSelect.value = "";
    this.setRadius("25");
    this.hideError(this.stateError);
    this.hideError(this.zipError);

    this.applied = null;
    this.pushUrlParams({});

    var self = this;
    this.fetchLocations({}).then(function () {
      self.updateViewFromState();
      self.closeSheet();
    });
  };

  /**
   * Debounced count-only preview for the mobile "Show N locations" Apply
   * button label. Uses the tentative (unapplied) form values and never
   * touches `applied`/the map/results. Falls back to a static "Apply
   * filters" label if the count can't be determined.
   */
  LocationsPage.prototype.schedulePreview = function () {
    clearTimeout(this.previewTimer);
    this.previewTimer = setTimeout(this.doPreview.bind(this), 250);
  };

  LocationsPage.prototype.doPreview = function () {
    if (!this.mobileQuery.matches) {
      this.setApplyLabel(null);
      return;
    }

    var values = this.collectFormValues();
    if (values.mode === "state" && !values.state) {
      this.setApplyLabel(null);
      return;
    }
    if (values.mode === "zip" && !/^\d{5}$/.test(values.zip)) {
      this.setApplyLabel(null);
      return;
    }

    var self = this;
    var apiParams = this.buildApiParams(values);
    this.api
      .request(this.locationsEndpoint, { method: "GET", params: apiParams })
      .then(function (res) {
        var data = Array.isArray(res) ? res : res.locations || res.data || [];
        self.setApplyLabel(data.length);
      })
      .catch(function () {
        self.setApplyLabel(null);
      });
  };

  LocationsPage.prototype.setApplyLabel = function (count) {
    if (!this.applyLabelEl) return;
    if (!this.mobileQuery.matches || count === null || count === undefined) {
      this.applyLabelEl.textContent = "Apply filters";
      return;
    }
    this.applyLabelEl.textContent =
      "Show " + count + " location" + (count === 1 ? "" : "s");
  };

  /**
   * Result block copy — exact rules from
   * design_handoff_locations_filter/README.md's "Result Block Copy" section.
   * Zero-result recovery copy is a provisional placeholder (marked
   * not-final in the handoff).
   */
  LocationsPage.prototype.updateResultBlock = function () {
    if (!this.resultHeadlineEl || !this.resultDetailEl) return;
    var count = this.filtered.length;
    var applied = this.applied;
    var headline, detail;

    if (!applied) {
      headline = "All " + count + " Merakey locations";
      detail =
        this.currentMode() === "zip"
          ? "Enter a ZIP code, then apply filters to search by distance"
          : "Choose a state, then apply filters to narrow the map";
    } else if (count === 0) {
      headline = "No locations match";
      detail = "Try a wider distance or a different service.";
    } else {
      headline = count + " location" + (count === 1 ? "" : "s") + " found";
      var serviceLabel = applied.service ? this.serviceLabel(applied.service) : "all services";
      if (applied.mode === "state") {
        detail = "In " + this.stateLabel(applied.state) + " · " + serviceLabel;
      } else {
        detail = "Located within " + applied.radius + " miles of " + applied.zip + " · " + serviceLabel;
      }
    }

    this.resultHeadlineEl.textContent = headline;
    this.resultDetailEl.textContent = detail;
  };

  LocationsPage.prototype.stateLabel = function (value) {
    if (!this.stateSelect) return value;
    var opt = Array.prototype.find.call(this.stateSelect.options, function (o) {
      return o.value === value;
    });
    return opt ? opt.text : value;
  };

  LocationsPage.prototype.serviceLabel = function (value) {
    if (!this.serviceSelect) return value;
    var opt = Array.prototype.find.call(this.serviceSelect.options, function (o) {
      return o.value === value;
    });
    return opt ? opt.text : value;
  };

  /**
   * Mobile filter sheet: open/close, scrim, focus trap, scroll lock.
   */
  LocationsPage.prototype.openSheet = function () {
    if (!this.filtersPanel || this.sheetOpen) return;
    this.filtersPanel.classList.add("locations__filters--open");
    if (this.scrimEl) this.scrimEl.hidden = false;
    document.body.style.overflow = "hidden";
    this.sheetOpen = true;
    this.lastFocusedBeforeSheet = document.activeElement;
    document.addEventListener("keydown", this._handleSheetKeydown);
    this.schedulePreview();

    var firstInput = this.filtersPanel.querySelector(
      'input[name="mode"]:checked, .filters__sheet-close'
    );
    if (firstInput) firstInput.focus();
  };

  LocationsPage.prototype.closeSheet = function () {
    if (!this.filtersPanel || !this.sheetOpen) return;
    this.filtersPanel.classList.remove("locations__filters--open");
    if (this.scrimEl) this.scrimEl.hidden = true;
    document.body.style.overflow = "";
    this.sheetOpen = false;
    document.removeEventListener("keydown", this._handleSheetKeydown);
    if (this.lastFocusedBeforeSheet && this.lastFocusedBeforeSheet.focus) {
      this.lastFocusedBeforeSheet.focus();
    }
  };

  LocationsPage.prototype.handleSheetKeydown = function (e) {
    if (e.key === "Escape") {
      this.closeSheet();
      return;
    }
    if (e.key !== "Tab" || !this.filtersPanel) return;

    var focusables = Array.prototype.filter.call(
      this.filtersPanel.querySelectorAll(
        'button, input, select, textarea, [href], [tabindex]:not([tabindex="-1"])'
      ),
      function (el) {
        return !el.disabled && !el.hidden && el.offsetParent !== null;
      }
    );
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  /**
   * Recenter map when a state is selected using zoomLevels.zoomLevels,
   * matching selected value against the `label` field, falling back to full state name.
   */
  LocationsPage.prototype.onStateChange = function () {
    if (!this.stateSelect) return false;
    var val = String(this.stateSelect.value || "").trim();
    if (!val) return false;

    // ensure map is present
    if (!this.mapManager || !this.mapManager.map) {
      try {
        this.mapManager.loadMap();
      } catch (e) {
        /* ignore */
      }
      if (!this.mapManager || !this.mapManager.map) return false;
    }

    var zl = this.zoomLevels;
    var match = null;

    if (zl && Array.isArray(zl.zoomLevels)) {
      match = zl.zoomLevels.find(function (z) {
        if (!z) return false;
        if (z.label && String(z.label).toLowerCase() === val.toLowerCase())
          return true;
        if (z.state && String(z.state).toLowerCase() === val.toLowerCase())
          return true;
        return false;
      });
    }

    if (!match) {
      var optText =
        (this.stateSelect.options[this.stateSelect.selectedIndex] || {}).text ||
        "";
      if (optText && zl && Array.isArray(zl.zoomLevels)) {
        match = zl.zoomLevels.find(function (z) {
          return (
            z &&
            z.state &&
            String(z.state).toLowerCase() === optText.toLowerCase()
          );
        });
      }
    }

    if (match) {
      var lat = parseFloat(match.latitude || match.lat);
      var lng = parseFloat(match.longitude || match.lng);
      var parsedZoom = parseInt(match.zoom, 10);
      var parsedZoomLevel = parseInt(match.zoomLevel, 10);
      var zoom = !Number.isNaN(parsedZoom)
        ? parsedZoom
        : !Number.isNaN(parsedZoomLevel)
        ? parsedZoomLevel
        : 6;

      if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
        try {
          // prefer a smooth pan if available
          this.mapManager.map.panTo({ lat: lat, lng: lng });
          // then set zoom
          this.mapManager.map.setZoom(zoom);
        } catch (e) {
          // fallback: create a new center/zoom with setCenter
          this.mapManager.map.setCenter({ lat: lat, lng: lng });
          this.mapManager.map.setZoom(zoom);
        }
        return true;
      }
      return false;
    }

    // no matching zoom record found - optional: console.warn
    // console.warn('No zoom level found for state:', val);
    return false;
  };

  /**
   * Recenter the map on the continental US at a zoom level that shows the
   * whole country. Used when a search has no zip or state selected.
   */
  LocationsPage.prototype.centerMapOnUS = function () {
    if (!this.mapManager || !this.mapManager.map) {
      try {
        this.mapManager.loadMap();
      } catch (e) {
        /* ignore */
      }
      if (!this.mapManager || !this.mapManager.map) return false;
    }

    var center = { lat: 39.5, lng: -98.35 };
    var zoom = 4;

    try {
      this.mapManager.map.panTo(center);
      this.mapManager.map.setZoom(zoom);
    } catch (e) {
      this.mapManager.map.setCenter(center);
      this.mapManager.map.setZoom(zoom);
    }

    return true;
  };

  /**
   * When the last fetch was a zipcode search (server returns the zip's
   * lat/lng in meta.zip), recenter and zoom the map on that point, using
   * the selected radius to pick a zoom level. Returns false if there's no
   * zip meta to center on, so callers can fall back to state-based centering.
   */
  LocationsPage.prototype.centerMapOnZip = function () {
    var meta = this.meta;
    if (!meta || !meta.zip) return false;

    var lat = parseFloat(meta.lat);
    var lng = parseFloat(meta.lng);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return false;

    if (!this.mapManager || !this.mapManager.map) {
      try {
        this.mapManager.loadMap();
      } catch (e) {
        /* ignore */
      }
      if (!this.mapManager || !this.mapManager.map) return false;
    }

    var radiusInput = this.form && this.form.elements["radius"];
    var radius = radiusInput ? parseInt(radiusInput.value, 10) : NaN;
    var zoom = this.radiusZoomLevels[radius] || this.radiusZoomLevels[25];

    try {
      this.mapManager.map.panTo({ lat: lat, lng: lng });
      this.mapManager.map.setZoom(zoom);
    } catch (e) {
      this.mapManager.map.setCenter({ lat: lat, lng: lng });
      this.mapManager.map.setZoom(zoom);
    }

    return true;
  };

  LocationsPage.prototype.loadDetails = function () {
    var self = this;
    return this.api
      .request(this.detailsEndpoint)
      .then(function (res) {
        var data = res.data || {};
        // support both { programs, states } and { services, states } naming
        var programs = data.programs || [];
        var states = data.states || [];

        fillSelect(self.serviceSelect, programs, "slug", "title", "All services");
        fillSelect(self.stateSelect, states, "value", "label", "Select a state");
        return res;
      })
      .catch(function (err) {
        console.error("Error loading details", err);
      });
  };

  LocationsPage.prototype.fetchLocations = function (params) {
    var self = this;
    params = params || {};
    var requestId = ++this.latestRequestId;
    // call endpoint; server may accept state/zip/distance/service/page
    return this.api
      .request(this.locationsEndpoint, { method: "GET", params: params })
      .then(function (res) {
        // a newer request has since been kicked off (e.g. a filter changed
        // again before this one finished) - drop this stale response
        // instead of clobbering the current pins with old data
        if (requestId !== self.latestRequestId) return res;

        // support either { locations: [...], meta: {...} } or direct array
        var locations = [];
        if (Array.isArray(res)) locations = res;
        else if (res.locations) locations = res.locations;
        else if (res.data) locations = res.data;
        self.allLocations = locations;
        self.meta = (res && res.meta) || {};
        self.filtered = self.allLocations.slice();
        self.currentPage = 1;
        return res;
      })
      .catch(function (err) {
        if (requestId !== self.latestRequestId) return;
        console.error("Error fetching locations", err);
        self.allLocations = [];
        self.filtered = [];
        self.meta = {};
      });
  };

  LocationsPage.prototype.updateViewFromState = function () {
    this.renderPage(this.currentPage);
    this.updatePagination();
    this.updateResultBlock();

    // Show pins for all filtered results (ignore pagination)
    var pinsToShow = Array.isArray(this.filtered) ? this.filtered.slice() : [];
    this.mapManager.addPins(pinsToShow);

    // Prefer centering on a zipcode search result, then a selected state;
    // if neither was searched, show the whole country
    if (!this.centerMapOnZip() && !this.onStateChange()) {
      this.centerMapOnUS();
    }
  };

  LocationsPage.prototype.renderPage = function (page) {
    var pageData = this.paginate(this.filtered, page, this.perPage);
    var html = "";
    if (!pageData.items.length) {
      html = '<div class="no-results">No locations found.</div>';
    } else {
      html = pageData.items
        .map(function (loc) {
          return createCard(loc);
        })
        .join("");
    }
    if (this.resultsEl) this.resultsEl.innerHTML = html;
    this.currentPage = pageData.page;
  };

  LocationsPage.prototype.changePage = function (page) {
    var total = Math.ceil(this.filtered.length / this.perPage) || 1;
    if (page < 1) page = 1;
    if (page > total) page = total;
    if (page === this.currentPage) return;
    this.currentPage = page;
    this.renderPage(page);
    this.setUrlPage(page);

    // Update pins based on filtering only (not pagination)
    this.mapManager.addPins(
      Array.isArray(this.filtered) ? this.filtered.slice() : []
    );

    this.updatePagination();
  };

  LocationsPage.prototype.paginate = function (items, page, perPage) {
    page = page || 1;
    perPage = perPage || this.perPage;
    var total = items.length;
    var pages = Math.max(1, Math.ceil(total / perPage));
    if (page > pages) page = pages;
    var start = (page - 1) * perPage;
    var end = start + perPage;
    return {
      items: items.slice(start, end),
      page: page,
      perPage: perPage,
      total: total,
      pages: pages,
    };
  };

  LocationsPage.prototype.updatePagination = function () {
    var meta = this.paginate(this.filtered, this.currentPage, this.perPage);
    var html = "";
    if (!this.paginationEl) return;
    // Previous button
    html +=
      '<li class="first"><button data-number="prev" class="pagination-link" aria-label="Previous Page">&#10094;</button></li>';
    // Page numbers: first, current +/- 2, last (ellipsis over any gaps)
    var current = meta.page;
    var start = Math.max(1, current - 2);
    var end = Math.min(meta.pages, current + 2);
    var pages = [];
    if (start > 1) pages.push(1);
    if (start > 2) pages.push("gap");
    for (var p = start; p <= end; p++) pages.push(p);
    if (end < meta.pages - 1) pages.push("gap");
    if (end < meta.pages) pages.push(meta.pages);
    pages.forEach(function (p) {
      if (p === "gap") {
        html +=
          '<li class="pagination-ellipsis" aria-hidden="true">&hellip;</li>';
        return;
      }
      html +=
        '<li><button data-page="' +
        p +
        '" class="pagination-link' +
        (p === current ? ' pagination-link--active" aria-current="page"' : '"') +
        ' aria-label="Page ' +
        p +
        '">' +
        p +
        "</button></li>";
    });
    // Next
    html +=
      '<li class="last"><button data-number="next" class="pagination-link" aria-label="Next Page">&#10095;</button></li>';
    this.paginationEl.innerHTML = html;
  };

  /**
   * Set ?page=# in the url, keeping any existing filter params. Page 1
   * drops the param so the default url stays clean.
   */
  LocationsPage.prototype.setUrlPage = function (page) {
    var params = parseQueryString(location.search || "");
    if (page > 1) params.page = page;
    else delete params.page;
    this.pushUrlParams(params);
  };

  LocationsPage.prototype.pushUrlParams = function (params) {
    params = params || {};
    var searchParams = new URLSearchParams();
    Object.keys(params).forEach(function (k) {
      if (params[k] !== undefined && params[k] !== "")
        searchParams.append(k, params[k]);
    });
    var newUrl =
      location.pathname +
      (searchParams.toString() ? "?" + searchParams.toString() : "");
    history.pushState({}, "", newUrl);
  };

  /**
   * Helper: escape html
   */
  function escapeHtml(str) {
    if (!str && str !== 0) return "";
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  /**
   * Helper: escape a value for use as an href, rejecting dangerous schemes
   * (javascript:, data:, vbscript:) so attacker-controlled URLs can't execute.
   */
  function sanitizeUrl(url) {
    var str = String(url || "").trim();
    if (!str) return "";
    if (/^(javascript|data|vbscript):/i.test(str)) return "";
    return escapeHtml(str);
  }

  /**
   * Helper: create card html from location object
   */
  function createCard(location) {
    return `
    <div class="location-card">
      <h4 class="location-card__title">${escapeHtml(location.title)}</h4>

      <ul class="location-card__details">
        <li class="location-card__item location-card__item--header">
          <p>Address Information</p>

          <ul class="location-card__details">
            <div class="location-card__address-container">
              <img class="location-card__icon" src="/assets/images/map-marker.svg" alt="blue map marker icon" />
              <div class="location-card__address">
                <li class="location-card__item">${escapeHtml(
                  location.address.address1
                )}</li>
                ${
                  location.address.address2
                    ? '<li class="location-card__item">' +
                      escapeHtml(location.address.address2) +
                      "</li>"
                    : ""
                }
                <li class="location-card__item">${escapeHtml(
                  location.address.city
                )}, ${escapeHtml(location.address.state)} ${escapeHtml(
      location.address.zip
    )}</li>
              </div>
            </div>
            <li class="location-card__item">
              <a href="${sanitizeUrl(
                location.directions
              )}" target="_BLANK">Get Directions</a>
            </li>
          </ul>
        </li>
        <li class="location-card__item location-card__item--header">
          <p>Contact Information</p>
          <ul class="location-card__details location-card__details--phone">
            ${
              location.phone !== null
                ? '<img class="location-card__icon" src="/assets/images/phone_icon_blue.svg" alt="blue map marker icon" /><li class="location-card__item">' +
                  escapeHtml(location.phone) +
                  "</li>"
                : ""
            }
          </ul>
        </li>

        ${
          Array.isArray(location.services) && location.services.length > 0
            ? '<li class="location-card__item location-card__item--header"><strong>Services: <ul>' +
              location.services
                .map((service) => {
                  if (service.url)
                    return (
                      '<li><a href="' +
                      sanitizeUrl(service.url) +
                      '">' +
                      escapeHtml(service.title) +
                      "</a></li>"
                    );
                  else return "<li>" + escapeHtml(service.title) + "</li>";
                })
                .join("") +
              "</ul></strong></li>"
            : ""
        }
      </ul>
    </div>
  `;
}

  function fillSelect(selectEl, arr, valueKey, labelKey, placeholder) {
    if (!selectEl) return;
    var html = '<option value="">' + escapeHtml(placeholder || "Select") + "</option>";
    if (!Array.isArray(arr)) arr = [];
    html += arr
      .map(function (o) {
        var v = o && o[valueKey] ? escapeHtml(o[valueKey]) : escapeHtml(o);
        var l = o && o[labelKey] ? escapeHtml(o[labelKey]) : escapeHtml(o);
        return '<option value="' + v + '">' + l + "</option>";
      })
      .join("");
    selectEl.innerHTML = html;
  }

  function parseQueryString(search) {
    var params = {};
    var str = (search || "").replace(/^\?/, "");
    if (!str) return params;
    str.split("&").forEach(function (pair) {
      var parts = pair.split("=");
      if (parts[0]) {
        params[decodeURIComponent(parts[0])] = decodeURIComponent(parts[1] || "");
      }
    });
    return params;
  }

  // Initialize only after both DOM and google.maps are ready
  var _lp;
  var _domReady = false;
  var _mapsReady = false;

  function _tryInit() {
    if (_domReady && _mapsReady) _lp.init();
  }

  window.initMap = function () {
    _mapsReady = true;
    _tryInit();
  };

  document.addEventListener("DOMContentLoaded", function () {
    _domReady = true;
    _lp = new LocationsPage({
      apiBase: "/api",
      mapId: "map",
      resultsId: "results",
      paginationListId: "pagination-list",
      perPage: 6,
      detailsEndpoint: "/details",
      locationsEndpoint: "/v2/locations",
    });
    _tryInit();
  });
})();
